// src/app/views/pericia/dialog-form-pericia/dialog-form-pericia.component.ts
import { ChangeDetectionStrategy, Component, Inject, Optional, OnInit, ChangeDetectorRef, Input } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

import { AccionesDialog } from '@models/base/identificadores.model';
import { ExpedienteService } from '@services/expediente/expediente.service';

import { ButtonCancelDialogComponent } from '@component/button-cancel-dialog/button-cancel-dialog.component';
import { ButtonCloseDialogComponent } from '@component/button-close-dialog/button-close-dialog.component';
import { ButtonAddComponent } from '@component/button-add/button-add.component';
import { ButtonSaveComponent } from '@component/button-save/button-save.component';
import { ButtonDeleteComponent } from '@component/button-delete/button-delete.component';

import { CreatePericiaRequest } from '@models/pericia/createPericiaRequest.model';
import { PericiaDto } from '@models/pericia/periciaDto.model';
import { UpdatePericiaRequest } from '@models/pericia/updatePericiaRequest.model';
import { PericiaService } from '@services/pericia/pericia.service';
import { EmpresaService } from '@services/empresa/empresa.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { TipoPericiaService } from '@services/tipoPericia/tipoPericia.service';
import { EstadoPericiaService } from '@services/estadoPericia/estadoPericia.service';

@Component({
  selector: 'app-dialog-form-pericia',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    NgxSpinnerModule,
    MatTableModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatExpansionModule,
    MatSelectModule,
    MatOptionModule,
    MatTabsModule,
    ButtonCancelDialogComponent,
    ButtonCloseDialogComponent,
    ButtonAddComponent,
    ButtonSaveComponent,
    ButtonDeleteComponent,
    MatAutocompleteModule
  ],
  templateUrl: './dialog-form-pericia.component.html',
  styleUrl: './dialog-form-pericia.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogFormPericiaComponent implements OnInit {
  @Input() idExpediente: any;
  formBuilder = new FormBuilder();
  periciaDto!: PericiaDto;
  tiposPericia: any[] = [];
  estadosPericia: any[] = [];
  empresas: any[] = [];
  expedientes: any[] = [];

  // Autocomplete para lugar
  lugarControl = new FormControl('');
  lugaresFrecuentes: string[] = [];
  filteredLugares!: Observable<string[]>;
  expedienteSeleccionado: any;

  // Getter para acceder a los controles del formulario fácilmente
  get f() { return this.form.controls; }

  accionesDialog = AccionesDialog;
  form!: FormGroup;
  localData: any;

  constructor(
    public dialogRef: MatDialogRef<DialogFormPericiaComponent>,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private periciaService: PericiaService,
    private tipoPericiaService: TipoPericiaService,
    private estadoPericiaService: EstadoPericiaService,
    private empresaService: EmpresaService,
    private expedienteService: ExpedienteService,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.localData = { ...data };
    this.initializeForm();
    this.setupLugarAutocomplete();
  }

  ngOnInit(): void {
    this.loadCatalogs();
    this.loadLugaresFrecuentes();
    this.setFormData();
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      Descripcion: ["", [Validators.required, Validators.maxLength(500)]],
      Observaciones: ["", [Validators.maxLength(1000)]],
      NotificacionRealizada: [false],
      Lugar: this.lugarControl,
      FechaRealizada: [""],
      FechaPropuesta: ["", [Validators.required]],
      FechaFinalizada: [""],
      IdExpediente: [""],
      IdEstadoPericia: ["", [Validators.required]],
      IdTipoPericia: ["", [Validators.required]],
      IdEmpresa: [""],
      UrlReunion: ["", [Validators.maxLength(500)]],
      FechaCedulaPresentacion: [""],
      FechaVencePresentacion: [""],
      FechaAceptaCargo: [""],
      FechaProponePericia: [""],
      FechaRealizaPericia: [""],
      Activo: [true]
    });
  }

  private setupLugarAutocomplete(): void {
    this.filteredLugares = this.lugarControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterLugares(value || ''))
    );
  }

  private _filterLugares(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.lugaresFrecuentes.filter(option =>
      option.toLowerCase().includes(filterValue)
    );
  }

  setFormData() {
    if (this.data?.accion === AccionesDialog.Modificar && this.data?.id) {
      this.form.patchValue({
        Descripcion: this.data.descripcion || '',
        Observaciones: this.data.observaciones || '',
        NotificacionRealizada: this.data.notificacionRealizada || false,
        FechaRealizada: this.data.fechaRealizada || null,
        FechaPropuesta: this.data.fechaPropuesta || null,
        FechaFinalizada: this.data.fechaFinalizada || null,
        IdExpediente: this.data.idExpediente || this.idExpediente || '',
        IdEstadoPericia: this.data.estadoPericia?.id || '',
        IdTipoPericia: this.data.tipoPericia?.id || '',
        IdEmpresa: this.data.empresa?.id || '',
        UrlReunion: this.data.urlReunion || '',
        FechaCedulaPresentacion: this.data.fechaCedulaPresentacion || null,
        FechaVencePresentacion: this.data.fechaVencePresentacion || null,
        FechaAceptaCargo: this.data.fechaAceptaCargo || null,
        FechaProponePericia: this.data.fechaProponePericia || null,
        FechaRealizaPericia: this.data.fechaRealizaPericia || null,
        Activo: this.data.activo !== undefined ? this.data.activo : true
      });

      this.lugarControl.setValue(this.data.lugar || '');
    }

    if (this.data?.accion === AccionesDialog.Agregar && this.data?.idExpediente) {
      this.form.patchValue({
        IdExpediente: this.data.idExpediente || this.idExpediente || ''
      });
    }

    this.cdr.markForCheck();
  }

  loadCatalogs() {
    // Cargar tipos de pericia
    this.spinner.show('spBusquedaTiposPericia');
    this.tipoPericiaService.getAll().subscribe({
      next: (tipos) => {
        this.tiposPericia = tipos;
        this.spinner.hide('spBusquedaTiposPericia');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.spinner.hide('spBusquedaTiposPericia');
        this.snackBar.showError(error, 'Error al cargar tipos de pericia');
        this.cdr.markForCheck();
      }
    });

    // Cargar estados de pericia
    this.spinner.show('spBusquedaEstados');
    this.estadoPericiaService.getAll().subscribe({
      next: (estados) => {
        this.estadosPericia = estados;
        this.spinner.hide('spBusquedaEstados');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.spinner.hide('spBusquedaEstados');
        this.snackBar.showError(error, 'Error al cargar estados de pericia');
        this.cdr.markForCheck();
      }
    });

    // Cargar empresas
    this.spinner.show('spBusquedaEmpresas');
    this.empresaService.getAll().subscribe({
      next: (empresas) => {
        this.empresas = empresas;
        this.spinner.hide('spBusquedaEmpresas');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.spinner.hide('spBusquedaEmpresas');
        this.snackBar.showError(error, 'Error al cargar empresas');
        this.cdr.markForCheck();
      }
    });
  }

  loadLugaresFrecuentes() {
    this.periciaService.getAllLugaresFrecuentes().subscribe({
      next: (lugares) => {
        this.lugaresFrecuentes = lugares;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.warn('No se pudieron cargar lugares frecuentes:', error);
      }
    });
  }

  agregar() {
    if (!this.form.valid) {
      this.markFormGroupTouched();
      return;
    }

    const createRequest: CreatePericiaRequest = new CreatePericiaRequest();
    this.mapFormToCreateRequest(createRequest);
    this.create(createRequest);
  }

  modificar() {
    if (!this.form.valid) {
      this.markFormGroupTouched();
      return;
    }

    const updateRequest: UpdatePericiaRequest = new UpdatePericiaRequest();
    updateRequest.id = this.data.id;
    this.mapFormToUpdateRequest(updateRequest);
    this.update(updateRequest);
  }

  eliminar() {
    if (!this.data?.id) {
      this.snackBar.showError("No se puede eliminar: ID no válido", "Error");
      return;
    }

    this.delete(this.data.id);
  }

  private mapFormToCreateRequest(request: CreatePericiaRequest) {
    request.descripcion = this.form.get("Descripcion")?.value || '';
    request.observaciones = this.form.get("Observaciones")?.value || '';
    request.notificacionRealizada = this.form.get("NotificacionRealizada")?.value || false;
    request.lugar = this.lugarControl.value || '';
    request.fechaRealizada = this.form.get("FechaRealizada")?.value || null;
    request.fechaPropuesta = this.form.get("FechaPropuesta")?.value || null;
    request.idExpediente = this.form.get("IdExpediente")?.value || null;
    request.idEstadoPericia = this.form.get("IdEstadoPericia")?.value || null;
    request.idTipoPericia = this.form.get("IdTipoPericia")?.value || null;
    request.urlReunion = this.form.get("UrlReunion")?.value || '';
    request.fechaCedulaPresentacion = this.form.get("FechaCedulaPresentacion")?.value || null;
    request.fechaVencePresentacion = this.form.get("FechaVencePresentacion")?.value || null;
    request.fechaAceptaCargo = this.form.get("FechaAceptaCargo")?.value || null;
    request.fechaProponePericia = this.form.get("FechaProponePericia")?.value || null;
    request.fechaRealizaPericia = this.form.get("FechaRealizaPericia")?.value || null;
    request.activo = this.form.get("Activo")?.value || true;
  }

  private mapFormToUpdateRequest(request: UpdatePericiaRequest) {
    request.descripcion = this.form.get("Descripcion")?.value || '';
    request.observaciones = this.form.get("Observaciones")?.value || '';
    request.notificacionRealizada = this.form.get("NotificacionRealizada")?.value || false;
    request.lugar = this.lugarControl.value || '';
    request.fechaRealizada = this.form.get("FechaRealizada")?.value || null;
    request.fechaPropuesta = this.form.get("FechaPropuesta")?.value || null;
    request.fechaFinalizada = this.form.get("FechaFinalizada")?.value || null;
    request.idExpediente = this.form.get("IdExpediente")?.value || null;
    request.idEstadoPericia = this.form.get("IdEstadoPericia")?.value || null;
    request.idTipoPericia = this.form.get("IdTipoPericia")?.value || null;
    request.urlReunion = this.form.get("UrlReunion")?.value || '';
    request.fechaCedulaPresentacion = this.form.get("FechaCedulaPresentacion")?.value || null;
    request.fechaVencePresentacion = this.form.get("FechaVencePresentacion")?.value || null;
    request.fechaAceptaCargo = this.form.get("FechaAceptaCargo")?.value || null;
    request.fechaProponePericia = this.form.get("FechaProponePericia")?.value || null;
    request.fechaRealizaPericia = this.form.get("FechaRealizaPericia")?.value || null;
    request.activo = this.form.get("Activo")?.value || true;
  }

  onExpedienteSelected(expediente: any): void {
    this.expedienteSeleccionado = expediente;
    if (expediente?.id) {
      this.form.get('IdExpediente')?.setValue(expediente.id);
    }
    this.cdr.markForCheck();
  }

  closeDialog() {
    this.dialogRef.close({ event: AccionesDialog.Cancelar });
  }

  create(createRequest: CreatePericiaRequest) {
    this.spinner.show("spCrearPericia");
    this.periciaService.create(createRequest)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spCrearPericia");
          this.snackBar.showSuccess("Pericia creada correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Agregar, data });
        },
        error: (error) => {
          this.spinner.hide("spCrearPericia");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  update(updateRequest: UpdatePericiaRequest) {
    this.spinner.show("spModificarPericia");
    this.periciaService.update(updateRequest)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spModificarPericia");
          this.snackBar.showSuccess("Pericia modificada correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Modificar, data });
        },
        error: (error) => {
          this.spinner.hide("spModificarPericia");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  delete(id: number) {
    this.spinner.show("spEliminarPericia");
    this.periciaService.delete(id)
      .subscribe({
        next: () => {
          this.spinner.hide("spEliminarPericia");
          this.snackBar.showSuccess("Pericia eliminada correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Eliminar });
        },
        error: (error) => {
          this.spinner.hide("spEliminarPericia");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  private markFormGroupTouched() {
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key)?.markAsTouched();
    });
    this.cdr.markForCheck();
  }

  trackByItems(index: number, item: any): any {
    return item?.id || index;
  }
}
